import React, { Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';

import { ICON_CLOSE } from 'assets/icons';
import Column from 'components/common/Column';
import CommonImage from 'components/common/CommonImage';
import Row from 'components/common/Row';
import Text from 'components/common/Text';
import ModalContainer from 'containers/ProductRegist/components/common/ModalContainer';
import { InvoiceState } from 'containers/SettlementRequest/graphQL/types';
import {
  requiredStatuses,
  CustomDate,
} from '../hooks/useSettlementRequestListHooks';
import DateInput from 'components/Input/DateInput';

const RegistFilterButton = styled.button<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 16px;
  border-radius: 8px;
  border: ${props =>
    props.$isSelected ? '1.5px solid #1b1c1e' : '1px solid #DBDCDF'};
  background: ${props => (props.$isSelected ? '#f7f7f8' : '#fff')};

  color: var(--gray-gray-09, #1b1c1e);

  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 142.857% */
  letter-spacing: -0.6px;
`;

interface SearchFilterContainerIProps {
  isMobile: boolean;
  termType: string;
  customDate: CustomDate;
  statusIn: InvoiceState[];
  handleResetInvoiceFilter: () => void;
  handleSetTermType: (type: string) => void;
  handleSetInvoiceState: (status: InvoiceState, isAllChecked?: boolean) => void;
  handleSetProgressingState: () => void;
  onAcceptFilter: () => void;
  setIsOpenSearchModal: Dispatch<SetStateAction<boolean>>;
  onClose: () => void;
  handleSetCustomDate: (params: Record<string, string>) => void;
}

const SearchFilterContainer: React.FC<SearchFilterContainerIProps> = props => {
  const {
    isMobile,
    termType,
    customDate,
    statusIn,
    handleResetInvoiceFilter,
    handleSetTermType,
    handleSetInvoiceState,
    handleSetProgressingState,
    onAcceptFilter,
    setIsOpenSearchModal,
    onClose,
    handleSetCustomDate,
  } = props;

  const isStatusAllChecked = requiredStatuses.every(status =>
    statusIn.includes(status),
  );

  const renderSettlementStatus = (): JSX.Element => {
    return (
      <Column gap="12px" align="flex-start">
        <Text color="#1B1C1E" size="16px">
          지급서 상태
        </Text>
        <Row
          gap="12px"
          justifyContent="flex-start"
          style={{ flexWrap: 'wrap' }}
        >
          <RegistFilterButton
            $isSelected={isStatusAllChecked}
            onClick={() => handleSetInvoiceState(InvoiceState.COMPLETED, true)}
          >
            전체
          </RegistFilterButton>
          <RegistFilterButton
            $isSelected={
              !isStatusAllChecked && statusIn.includes(InvoiceState.CANCELLED)
            }
            onClick={() => handleSetInvoiceState(InvoiceState.CANCELLED)}
          >
            지급 취소
          </RegistFilterButton>
          <RegistFilterButton
            $isSelected={
              !isStatusAllChecked && statusIn.includes(InvoiceState.COMPLETED)
            }
            onClick={() => handleSetInvoiceState(InvoiceState.COMPLETED)}
          >
            지급 완료
          </RegistFilterButton>
          <RegistFilterButton
            $isSelected={
              !isStatusAllChecked &&
              (statusIn.includes(InvoiceState.DRAFT_COMPLETED) ||
                statusIn.includes(InvoiceState.PROGRESSING) ||
                statusIn.includes(InvoiceState.READY))
            }
            onClick={handleSetProgressingState}
          >
            지급 진행 중
          </RegistFilterButton>
          <RegistFilterButton
            $isSelected={
              !isStatusAllChecked && statusIn.includes(InvoiceState.FAILED)
            }
            onClick={() => handleSetInvoiceState(InvoiceState.FAILED)}
          >
            지급 실패
          </RegistFilterButton>
          <RegistFilterButton
            $isSelected={
              !isStatusAllChecked && statusIn.includes(InvoiceState.RECEIVED)
            }
            onClick={() => handleSetInvoiceState(InvoiceState.RECEIVED)}
          >
            회수 완료
          </RegistFilterButton>
          <RegistFilterButton
            $isSelected={
              !isStatusAllChecked &&
              statusIn.includes(InvoiceState.RECEIVE_REQUESTED)
            }
            onClick={() =>
              handleSetInvoiceState(InvoiceState.RECEIVE_REQUESTED)
            }
          >
            회수 요청
          </RegistFilterButton>
        </Row>
      </Column>
    );
  };

  const renderCustomDate = (type: 'start' | 'end'): JSX.Element => {
    return (
      <Row gap="6px" style={{ width: '50%' }}>
        <Text
          color={customDate[type] !== '' ? '#414B56' : '#c2c4c8'}
          weight={400}
        >
          {customDate[type] !== '' ? customDate[type] : '기간 선택'}{' '}
        </Text>
        <DateInput
          isMinDate
          onChange={(e: any) => {
            handleSetCustomDate({
              [type]: e.target.value,
            });
          }}
        />
      </Row>
    );
  };

  const renderSettlementTerm = (): JSX.Element => {
    return (
      <Column gap="12px" align="flex-start">
        <Text color="#1B1C1E" size="16px">
          조회 기간
        </Text>
        <Row
          gap="12px"
          justifyContent="flex-start"
          style={{ flexWrap: 'wrap' }}
        >
          <RegistFilterButton
            $isSelected={termType === 'all'}
            onClick={() => handleSetTermType('all')}
          >
            전체
          </RegistFilterButton>
          <RegistFilterButton
            $isSelected={termType === '1month'}
            onClick={() => handleSetTermType('1month')}
          >
            최근 1개월
          </RegistFilterButton>
          <RegistFilterButton
            $isSelected={termType === '6month'}
            onClick={() => handleSetTermType('6month')}
          >
            최근 6개월
          </RegistFilterButton>
          <RegistFilterButton
            $isSelected={termType === '1year'}
            onClick={() => handleSetTermType('1year')}
          >
            최근 1년
          </RegistFilterButton>
          <RegistFilterButton
            $isSelected={termType === '5year'}
            onClick={() => handleSetTermType('5year')}
          >
            최근 5년
          </RegistFilterButton>
          <RegistFilterButton
            $isSelected={termType === 'custom'}
            onClick={() => handleSetTermType('custom')}
          >
            직접입력
          </RegistFilterButton>
          {termType === 'custom' && (
            <Row
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #eaebec',
                borderRadius: '8px',
              }}
            >
              {renderCustomDate('start')}
              <Text color="#414B56" weight={700}>
                ~
              </Text>
              {renderCustomDate('end')}
            </Row>
          )}
        </Row>
      </Column>
    );
  };

  return (
    <ModalContainer
      isMobile={isMobile}
      isAbled={true}
      onCloseText="초기화"
      onClose={handleResetInvoiceFilter}
      onAcceptText="적용"
      onClick={() => {
        onAcceptFilter();

        setIsOpenSearchModal(false);
      }}
    >
      <Column style={{ padding: '0 20px' }}>
        <Row style={{ padding: '16px 0', width: '100%' }}>
          <CommonImage
            src={ICON_CLOSE}
            onClick={onClose}
            style={{ position: 'absolute', left: '20px' }}
          />
          <Text color="#000" size="16px" weight={600}>
            검색
          </Text>
        </Row>
        <Column gap="32px" style={{ padding: '12px 0' }} align="flex-start">
          {renderSettlementStatus()}
          {renderSettlementTerm()}
        </Column>
      </Column>
    </ModalContainer>
  );
};

export default SearchFilterContainer;
